import './Accordion.scss';

import classnames from 'classnames';
import React, { useState } from 'react';

import { AccordionContext, useAccordionContext } from './AccordionContext';
import { useWindowSize } from './useWindowSize';
import { formatHtml, getBreakpointValue, renderUI } from './util';

export interface AccordionProps {
  accordionColClassName?: string;
  alwaysOpened?: boolean;
  children?: React.ReactNode;
  className?: string;
  columnLayoutFrom?: 'md' | 'lg' | 'xl' | 'xxl';
  id: string;
  itemBodyClassName?: string;
  itemButtonClassName?: string;
  itemClassName?: string;
  itemHeaderClassName?: string;
  withoutXPadding?: boolean;
}

export const Accordion = ({
  accordionColClassName,
  alwaysOpened = false,
  children,
  className,
  columnLayoutFrom,
  id,
  itemBodyClassName,
  itemButtonClassName,
  itemClassName,
  itemHeaderClassName,
  withoutXPadding,
}: AccordionProps) => {
  const [horizontal, setHorizontal] = useState(false);
  const windowSize = useWindowSize();

  React.useEffect(() => {
    if (columnLayoutFrom && windowSize.width >= getBreakpointValue(columnLayoutFrom)) {
      setHorizontal(true);
    } else {
      setHorizontal(false);
    }
  }, [columnLayoutFrom, windowSize.width]);

  return renderUI({
    bs5: (
      <div className={classnames('accordion', 'Accordion', className, { 'horizontal-layout': horizontal })} id={id}>
        <AccordionContext
          accordionColClassName={accordionColClassName}
          alwaysOpened={alwaysOpened}
          columnLayoutFrom={columnLayoutFrom}
          id={id}
          itemBodyClassName={itemBodyClassName}
          itemButtonClassName={itemButtonClassName}
          itemClassName={itemClassName}
          itemHeaderClassName={itemHeaderClassName}
          withoutXPadding={withoutXPadding}
        >
          <div className="row">{children}</div>
        </AccordionContext>
      </div>
    ),
  });
};

export interface AccordionItemProps {
  accordionBodyClassName?: string;
  accordionButtonClassName?: string;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  headingClass?: string;
  headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  id: string;
  show?: boolean;
  title: string | React.ReactElement;
}

export const AccordionItem = ({
  accordionBodyClassName,
  accordionButtonClassName,
  children,
  className,
  disabled,
  headingClass,
  headingTag,
  id,
  show = false,
  title,
}: AccordionItemProps) => {
  const accordionContext = useAccordionContext();
  return renderUI({
    bs5: (
      <div
        className={classnames(
          accordionContext.columnLayoutFrom ? `col-${accordionContext.columnLayoutFrom}` : 'col-12',
          accordionContext.accordionColClassName,
          className
        )}
      >
        <div className={classnames('accordion-item', accordionContext.itemClassName)}>
          {/* <p className={classnames('accordion-header h3', accordionContext.itemHeaderClassName)} id={`heading-${id}`}>
            <button
              aria-controls={`collapse-${id}`}
              aria-expanded={show}
              className={classnames(
                'accordion-button',
                { collapsed: !show },
                accordionContext.itemButtonClassName,
                {
                  'px-0': accordionContext.withoutXPadding,
                },
                accordionButtonClassName
              )}
              data-bs-target={`#collapse-${id}`}
              data-bs-toggle="collapse"
              disabled={disabled}
              style={{ pointerEvents: disabled ? 'none' : undefined }}
              type="button"
            >
              <span>{typeof title === 'string' ? formatHtml(title) : title}</span>
            </button>
          </p> */}
          {React.createElement(
            headingTag || 'p',
            {
              className: classnames('accordion-header', headingClass, accordionContext.itemHeaderClassName),
              id: `heading-${id}`,
            },
            // eslint-disable-next-line react/forbid-elements
            <button
              aria-controls={`collapse-${id}`}
              aria-expanded={show}
              className={classnames(
                'accordion-button',
                { collapsed: !show },
                accordionContext.itemButtonClassName,
                {
                  'px-0': accordionContext.withoutXPadding,
                },
                accordionButtonClassName
              )}
              data-bs-target={`#collapse-${id}`}
              data-bs-toggle="collapse"
              disabled={disabled}
              style={{ pointerEvents: disabled ? 'none' : undefined }}
              type="button"
            >
              <span>{typeof title === 'string' ? formatHtml(title) : title}</span>
            </button>
          )}
          <div
            aria-labelledby={`heading-${id}`}
            className={classnames('accordion-collapse collapse', { show: show })}
            data-bs-parent={!accordionContext.alwaysOpened ? `#${accordionContext.id}` : undefined}
            id={`collapse-${id}`}
          >
            <div
              className={classnames(
                'accordion-body',
                accordionContext.itemBodyClassName,
                {
                  'px-0': accordionContext.withoutXPadding,
                },
                accordionBodyClassName
              )}
            >
              {!disabled && children}
            </div>
          </div>
        </div>
      </div>
    ),
  });
};
